import { useEffect, useRef, useState, useCallback } from 'react';
import { cfCooldownRemainingMs } from '../api/apiClient';

/**
 * useMessaging — single WebSocket to the backend `/ws/messages`
 * endpoint, shared by inbox notifications, typing indicators, read
 * receipts and Messenger Calls V1 (WebRTC signalling).
 *
 * Lifecycle
 * ---------
 *   • Always derives the WS origin from the current backend URL using
 *     a strict protocol resolver — `wss://` when the page is on
 *     https (i.e. all deployed environments), `ws://` only when the
 *     backend is plain http. This matches the dynamic backend URL
 *     resolver in `public/index.html`.
 *
 *   • Sends `?token=<JWT>` from `localStorage.access_token` on the
 *     handshake. The backend validates the JWT directly; the
 *     http-only cookie isn't readable from JS, so the login response
 *     body's `access_token` is the source of truth.
 *
 *   • Reconnects with capped exponential backoff (1 s → 30 s) on
 *     transport errors, BUT silences itself forever after a 1008 auth
 *     close — re-logging-in is the only valid recovery and we never
 *     want a never-authed socket to thrash.
 *
 *   • Exposes:
 *       - `isConnected` — true ONLY when readyState === OPEN
 *       - `wsState` — 'connecting' | 'open' | 'reconnecting' | 'closed'
 *       - `newMessage` — last received message frame
 *       - `typingUsers`
 *       - `sendTypingIndicator(isTyping)`
 *       - `wsRef` — live ref to the WebSocket so calls can piggy-back
 */

const BASE_BACKOFF_MS = 1000;        // 1s
const MAX_BACKOFF_MS  = 30 * 1000;   // 30s
// Hard cap on consecutive reconnect attempts. After this we stop trying
// until the next mount/login. Prevents the "Insufficient resources"
// browser warning that appears when a stale token / unavailable WS
// upgrade causes us to thrash a connection every 30s indefinitely.
const MAX_WS_FAILURES = 8;

// Resolve the WS origin once. We use `window.__BACKEND_URL__` (set in
// public/index.html before any module loads) so a single bundle works
// for preview, custom-domain prod, and the bare `*.emergent.host`
// origin. NEVER fall back to `ws:` from `https:` — that mismatch is
// what blocks Safari/Firefox in production.
function _resolveWsBase() {
  let backend = window.__BACKEND_URL__ || '';
  if (!backend && typeof window !== 'undefined') {
    backend = window.location.origin;
  }
  if (backend.startsWith('https://')) return 'wss://' + backend.slice('https://'.length);
  if (backend.startsWith('http://'))  return 'ws://'  + backend.slice('http://'.length);
  // Fallback — derive from current location.
  const proto = (typeof window !== 'undefined' && window.location?.protocol === 'https:') ? 'wss:' : 'ws:';
  return proto + '//' + (typeof window !== 'undefined' ? window.location.host : '');
}

function _getAccessToken() {
  try {
    return window.localStorage.getItem('access_token') || '';
  } catch {
    return '';
  }
}

export const useMessaging = (conversationId = null, { onSignal, enabled = true } = {}) => {
  const wsRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  // 'connecting' before the first onopen; 'reconnecting' after a drop;
  // 'closed' when we've given up (auth rejected). The header pill reads
  // this to show the "Reconnecting…" status without re-introducing the
  // toast spam from the previous patch.
  const [wsState, setWsState] = useState('connecting');
  const [newMessage, setNewMessage] = useState(null);
  const [typingUsers, setTypingUsers] = useState({});

  const reconnectTimeoutRef = useRef(null);
  const failureCount = useRef(0);
  const wsGaveUp = useRef(false);
  const connectRef = useRef(null);
  // Keep the latest onSignal in a ref so the WS message handler — bound
  // exactly once per connection — always dispatches to the freshest
  // implementation without us having to tear down and rebuild the
  // socket every time the consumer rerenders.
  const onSignalRef = useRef(onSignal);
  useEffect(() => { onSignalRef.current = onSignal; }, [onSignal]);

  const scheduleReconnect = useCallback(() => {
    if (wsGaveUp.current) {
      setWsState('closed');
      return;
    }
    // Stop after too many consecutive failures — keeps the console
    // clean and protects the browser from running out of resource
    // slots when the backend WS is unavailable for an extended period.
    if (failureCount.current >= MAX_WS_FAILURES) {
      wsGaveUp.current = true;
      setWsState('closed');
      return;
    }
    if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    const backoffDelay = Math.min(
      BASE_BACKOFF_MS * Math.pow(2, Math.max(0, failureCount.current - 1)),
      MAX_BACKOFF_MS,
    );
    // Iter-rescue 2026-02 — if a CF/origin storm just happened over
    // HTTP, also delay the WS reconnect by at least the cooldown
    // remainder. Re-handshaking through the same Cloudflare edge
    // while it's already struggling only digs the hole deeper.
    const cfDelay = cfCooldownRemainingMs();
    const delay = Math.max(backoffDelay, cfDelay);
    if (cfDelay > 0) {
      // eslint-disable-next-line no-console
      console.info('[ccuk-network] messaging-ws-backoff', { delay_ms: delay, cf_cooldown_ms: cfDelay });
    }
    setWsState('reconnecting');
    reconnectTimeoutRef.current = setTimeout(
      () => connectRef.current && connectRef.current(),
      delay,
    );
  }, []);

  const connect = useCallback(() => {
    if (!enabled) return;
    if (wsGaveUp.current) return;
    // Idempotent guard — never open a second socket if one is OPEN
    // or still CONNECTING. Critical for React 18 StrictMode dev
    // double-mount (effect runs cleanup-then-mount immediately, and
    // a window.focus/online listener can fire again before the close
    // has propagated to readyState). Without this guard a brief
    // burst of connection attempts hits the browser's per-host WS
    // cap and yields "Insufficient resources".
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    if (wsRef.current?.readyState === WebSocket.CONNECTING) return;
    // Drop any half-open socket from a previous attempt — `new
    // WebSocket()` doesn't clean up old handles automatically.
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      try {
        wsRef.current.onclose = null;
        wsRef.current.onerror = null;
        wsRef.current.close();
      } catch { /* noop */ }
    }

    const token = _getAccessToken();
    if (!token) {
      // No token in localStorage — user is logged out OR the login
      // response didn't return access_token. Don't even attempt the
      // socket because the backend will close us with code 1008
      // and we'd waste an exponential-backoff slot.
      wsGaveUp.current = true;
      setWsState('closed');
      return;
    }
    const url = _resolveWsBase() + '/api/ws/messages?token=' + encodeURIComponent(token);

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;
      setWsState('connecting');

      ws.onopen = () => {
        failureCount.current = 0; // reset on successful open
        setIsConnected(true);
        setWsState('open');
        // Conversation-id 'join' is sent by the dedicated effect
        // below (after ws state flips to 'open') so the join is
        // re-sent on reconnect AND on conversation switch — without
        // tying the socket lifecycle itself to conversationId.
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          switch (data.type) {
            case 'connected':
              // Server-side ack — nothing to do, the open handler
              // already flipped state.
              break;

            case 'new_message':
              // Main delivery path: hand the actual message document to
              // MessagesPage so it can append the bubble, mark delivered,
              // mark read, and refresh the inbox.
              setNewMessage(data.message);
              break;

            case 'message_delivered': {
              // Receipt frames must also be surfaced through `newMessage`.
              // MessagesPage already knows how to patch delivered_to[] but
              // the old hook swallowed everything except `new_message`, so
              // the UI stayed stuck on one grey tick until a reload/refetch.
              const deliveredTo = Array.isArray(data.delivered_to)
                ? data.delivered_to
                : [data.delivered_to || data.user_id || data.delivered_by].filter(Boolean);

              setNewMessage({
                ...data,
                type: 'message_delivered',
                delivered_to: deliveredTo,
              });
              break;
            }

            case 'message_read': {
              // Normalise read receipts to the shape MessagesPage expects:
              // { type: 'message_read', message_id, read_by }.
              setNewMessage({
                ...data,
                type: 'message_read',
                read_by: data.read_by || data.user_id || data.read_by_user_id,
              });
              break;
            }

            case 'read_receipt': {
              // Backwards-compatible alias used by some earlier backend
              // revisions. Convert it into the newer message_read frame so
              // the page has one receipt path to maintain.
              setNewMessage({
                ...data,
                type: 'message_read',
                message_id: data.message_id || data.id,
                read_by: data.read_by || data.user_id || data.read_by_user_id,
              });
              break;
            }

            case 'message_unsent':
            case 'message_edited':
            case 'message_admin_deleted':
            case 'conversation_admin_deleted':
              // These are also handled in MessagesPage. Do not treat them
              // as call signals; expose them as realtime message events so
              // existing bubbles patch in place without refresh.
              // `message_admin_deleted` / `conversation_admin_deleted`
              // are emitted by /api/admin/messages/{id} and
              // /api/admin/conversations/{id} — both participants get
              // the frame so the floating popout + full page can drop
              // the bubble / conversation immediately.
              setNewMessage(data);
              break;

            case 'typing':
              setTypingUsers(prev => ({ ...prev, [data.user_id]: data.is_typing }));
              setTimeout(() => {
                setTypingUsers(prev => ({ ...prev, [data.user_id]: false }));
              }, 3000);
              break;

            // Messenger Calls V1 — every call / WebRTC frame is forwarded
            // verbatim to the consumer's `onSignal` handler. Keeping the
            // dispatch here (rather than a second WS) means we re-use the
            // existing auth handshake + reconnect logic for free.
            case 'call_invite':
            case 'call_accepted':
            case 'call_declined':
            case 'call_cancelled':
            case 'call_ended':
            case 'call_missed':
            case 'webrtc_offer':
            case 'webrtc_answer':
            case 'ice_candidate':
              if (onSignalRef.current) onSignalRef.current(data);
              break;
            default:
              // Pass through to the optional signal handler so future
              // frame types (read_receipts, presence, etc.) can be
              // consumed without re-touching this hook.
              if (onSignalRef.current) onSignalRef.current(data);
          }
        } catch {
          // Non-fatal parse error; ignore to keep console clean.
        }
      };

      ws.onerror = () => {
        // Intentionally do NOT call ws.close() here. The browser
        // emits onclose automatically; closing from inside onerror
        // while the socket is still CONNECTING produces the noisy
        // "WebSocket is closed before the connection is established."
        // console warning. Let onclose handle the reconnect schedule.
      };

      ws.onclose = (ev) => {
        setIsConnected(false);
        // 1008 = backend rejected the handshake (missing / invalid /
        // expired token). Re-attempting will fail identically — stop
        // until the user re-authenticates.
        if (ev && ev.code === 1008) {
          wsGaveUp.current = true;
          setWsState('closed');
          return;
        }
        failureCount.current += 1;
        scheduleReconnect();
      };
    } catch {
      failureCount.current += 1;
      scheduleReconnect();
    }
    // Iter156 — connect MUST NOT depend on conversationId. Tying the
    // socket lifecycle to conversation switches caused every Inbox
    // click to close-and-reopen the WebSocket, which in turn
    //   (a) triggered "Insufficient resources" after ~6 rapid clicks
    //       (browsers cap concurrent WS handshakes per host), and
    //   (b) dropped in-flight call signalling frames (webrtc_offer /
    //       webrtc_answer / ice_candidate) during a live call,
    //       producing the iter155 symptom of "self-view OK,
    //       remote-view never arrives". The conversation 'join' is
    //       now sent by the dedicated effect below — safe on every
    //       (ws_open) AND on every conversation switch.
  }, [enabled, scheduleReconnect]);

  // Send a 'join' frame whenever the socket is open AND the user
  // has selected a conversation. Re-fires on every reconnect (state
  // transitions to 'open' again) and on every conversation change,
  // without ever recreating the WebSocket.
  useEffect(() => {
    if (wsState !== 'open' || !conversationId) return;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify({ type: 'join', conversation_id: conversationId }));
    } catch { /* ignore */ }
  }, [conversationId, wsState]);

  useEffect(() => { connectRef.current = connect; }, [connect]);

  useEffect(() => {
    if (!enabled) {
      setIsConnected(false);
      setWsState('closed');
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.onerror = null;
        try { wsRef.current.close(); } catch { /* noop */ }
        wsRef.current = null;
      }
      return undefined;
    }

    connect();

    // Auto-recover the socket when the browser tab/app regains focus
    // or the network comes back online. Without this, iPhone + Android
    // users who background the browser (or temporarily lose signal)
    // can find the WS in the 'closed' state and need to reload the
    // page to see new messages. Resetting `wsGaveUp` + `failureCount`
    // here pairs with the existing scheduleReconnect logic to retry
    // immediately when the user is most likely to be looking at the
    // app again.
    const tryRecover = () => {
      const ws = wsRef.current;
      const open = ws && ws.readyState === WebSocket.OPEN;
      if (open) return; // socket already healthy
      // Reset the "gave up" flag so connect() doesn't short-circuit.
      // We don't fully zero failureCount — keep a small floor so a
      // genuine outage doesn't loop without backoff — but we do reset
      // to 0 after a successful onopen elsewhere.
      wsGaveUp.current = false;
      failureCount.current = 0;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      connectRef.current && connectRef.current();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') tryRecover();
    };
    window.addEventListener('focus', tryRecover);
    window.addEventListener('online', tryRecover);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      window.removeEventListener('focus', tryRecover);
      window.removeEventListener('online', tryRecover);
      document.removeEventListener('visibilitychange', onVisibility);
      if (wsRef.current) {
        // Null out handlers so close() doesn't trigger another reconnect.
        wsRef.current.onclose = null;
        wsRef.current.onerror = null;
        try { wsRef.current.close(); } catch { /* noop */ }
      }
    };
  }, [connect, enabled]);

  const sendTypingIndicator = useCallback((isTyping) => {
    if (wsRef.current?.readyState === WebSocket.OPEN && conversationId) {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        conversation_id: conversationId,
        is_typing: isTyping,
      }));
    }
  }, [conversationId]);

  return {
    isConnected,
    wsState,
    newMessage,
    typingUsers,
    sendTypingIndicator,
    // Exposed so call signalling can piggy-back on the same socket
    // (sending webrtc_offer/answer + ice_candidate frames).
    wsRef,
  };
};
